import React from 'react';
import { CloudOutlined, DeploymentUnitOutlined, ReloadOutlined } from '@ant-design/icons';
import { Divider, Flex, theme, Typography } from 'antd';

import { useSelector } from '../../../hooks/usePrerenderUser';

type TrialBenefitsVariant = 'starter' | 'legacy';

type TrialBenefitsListProps = {
  variant?: TrialBenefitsVariant;
};

const COPY: Record<
  TrialBenefitsVariant,
  {
    allowanceDetail: string;
    cacheTitle: string;
    cacheDetail: string;
    bonus: boolean;
    apiDetail: string;
  }
> = {
  starter: {
    allowanceDetail: 'To get your pages indexed quickly.',
    cacheTitle: 'Flexible cache expiration',
    cacheDetail: 'Choose between 24 hours to 7 days, depending on your site’s needs.',
    bonus: false,
    apiDetail: 'To manage caching and integrations.',
  },
  legacy: {
    allowanceDetail: 'Ensure every crawler can see your pages.',
    cacheTitle: 'Flexible caching options',
    cacheDetail: 'Avoid showing outdated content in search results.',
    bonus: true,
    apiDetail: 'Enjoy this extra feature for free and save big on developer costs.',
  },
};

export const TrialBenefitsList = ({ variant = 'starter' }: TrialBenefitsListProps) => {
  const { token } = theme.useToken();
  const renderLimit = useSelector((state) => state.prerenderUser.plan.limit);
  const copy = COPY[variant];

  return (
    <Flex vertical gap={10} style={{ marginTop: token.marginLG }}>
      <Flex align="start" gap={token.sizeSM}>
        <ReloadOutlined style={{ fontSize: token.fontSizeHeading3, color: token.colorPrimary }} />
        <div>
          <Typography.Paragraph strong style={{ margin: 0 }}>
            {`${renderLimit.toLocaleString('en-US')} renders`}
          </Typography.Paragraph>
          <Typography.Paragraph style={{ margin: 0, fontSize: token.fontSizeSM }}>
            {copy.allowanceDetail}
          </Typography.Paragraph>
        </div>
      </Flex>
      <Flex align="start" gap={token.sizeSM}>
        <CloudOutlined style={{ fontSize: token.fontSizeHeading3, color: token.colorPrimary }} />
        <div>
          <Typography.Paragraph strong style={{ margin: 0 }}>
            {copy.cacheTitle}
          </Typography.Paragraph>
          <Typography.Paragraph style={{ margin: 0, fontSize: token.fontSizeSM }}>
            {copy.cacheDetail}
          </Typography.Paragraph>
        </div>
      </Flex>
      {copy.bonus && (
        <Divider plain style={{ margin: 0 }}>
          Bonus
        </Divider>
      )}
      <Flex align="start" gap={token.sizeSM}>
        <DeploymentUnitOutlined style={{ fontSize: token.fontSizeHeading3, color: token.colorPrimary }} />
        <div>
          <Typography.Paragraph strong style={{ margin: 0 }}>
            Full API access
          </Typography.Paragraph>
          <Typography.Paragraph style={{ margin: 0, fontSize: token.fontSizeSM }}>
            {copy.apiDetail}
          </Typography.Paragraph>
        </div>
      </Flex>
    </Flex>
  );
};
